import { useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Logo } from './Logo';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/store/auth';
import { cn } from '@/lib/cn';

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
  links: ReadonlyArray<{ to: string; key: string }>;
}

export function MobileMenu({ open, onClose, links }: MobileMenuProps) {
  const { t } = useTranslation();
  const { isAuthenticated, logout } = useAuthStore();

  // Menyu ochilganda body scroll'ni bloklash
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] md:hidden" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in-fast" onClick={onClose} />
      <div className="absolute right-0 top-0 flex h-full w-72 flex-col bg-elevated p-6 shadow-lg animate-slide-in-right">
        <div className="mb-8 flex items-center justify-between">
          <Logo size="sm" />
          <button onClick={onClose} className="btn btn-ghost rounded-DEFAULT p-2" aria-label={t('nav.closeMenu')}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex flex-col gap-1">
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  'rounded-DEFAULT px-4 py-3 font-medium transition-colors',
                  isActive ? 'bg-surface text-content' : 'text-content-secondary hover:bg-surface',
                )
              }
            >
              {t(link.key)}
            </NavLink>
          ))}
        </nav>

        <hr className="my-4 border-line" />

        {isAuthenticated ? (
          <div className="flex flex-col gap-2">
            <Link to="/profile" onClick={onClose} className="rounded-DEFAULT px-4 py-3 font-medium hover:bg-surface">
              {t('nav.profile')}
            </Link>
            <Button
              variant="secondary"
              onClick={() => {
                logout();
                onClose();
              }}
            >
              {t('nav.logout')}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <Link to="/login" onClick={onClose} className="rounded-DEFAULT px-4 py-3 font-medium hover:bg-surface">
              {t('nav.login')}
            </Link>
            <Link to="/register" onClick={onClose}>
              <Button className="w-full">{t('nav.register')}</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
